import { useState } from 'react';
import { useDragPan } from '@/lib/use-drag-pan';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  CircleSlash,
  FileText,
  Filter,
  MessageSquare,
  Radar,
  Send,
  Snowflake,
  Thermometer,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { KanbanLane, KanbanLaneEmpty } from '@/ui/primitives';
import { CrmCard } from './CrmCard';
import { ESTAGIO_LABEL, type Coluna, type Estagio } from '../schemas';
import type { Card as CardData } from '../schemas';

interface Props {
  colunas: Coluna[];
  onCardClick: (card: CardData) => void;
}

const PAGE_SIZE = 10;

const COLUNA_ICON: Record<Estagio, LucideIcon> = {
  sinal_detectado: Radar,
  triagem: Filter,
  qualificacao: Thermometer,
  falso_positivo_servico: AlertTriangle,
  dossie: FileText,
  abordagem: MessageSquare,
  proposta: Send,
  congelado: Snowflake,
  fechado_ganho: CheckCircle2,
  fechado_perdido: XCircle,
  recusado: CircleSlash,
  descartado: XCircle,
};

export function CrmKanban({ colunas, onCardClick }: Props) {
  const [visibleByColumn, setVisibleByColumn] = useState<Record<string, number>>({});
  const pan = useDragPan<HTMLDivElement>();

  function showMore(colunaId: string) {
    setVisibleByColumn((prev) => ({
      ...prev,
      [colunaId]: (prev[colunaId] ?? PAGE_SIZE) + PAGE_SIZE,
    }));
  }

  return (
    <div
      ref={pan.ref}
      onMouseDown={pan.onMouseDown}
      className={`overflow-x-auto -mx-8 pb-6 select-none ${pan.cursorClass}`}
    >
      {/* Sem min-h aqui: a altura das colunas é calculada no KanbanLane a partir
          do <main>. Um mínimo na linha só criava linha mais alta que as colunas
          — vão morto abaixo delas. Sem padding-top pelo mesmo motivo do board
          compartilhado: o respiro vem do gap da página. */}
      <div className="flex gap-3 items-start px-8 w-max">
        {colunas.map((c) => {
          const Icon = COLUNA_ICON[c.id] ?? Radar;
          const total = c.processos.length;
          const visiveis = visibleByColumn[c.id] ?? PAGE_SIZE;
          const cards = total > visiveis ? c.processos.slice(0, visiveis) : c.processos;
          const restantes = total - cards.length;
          return (
            <KanbanLane
              key={c.id}
              data-pan-skip
              icon={Icon}
              title={ESTAGIO_LABEL[c.id] ?? c.nome}
              count={total}
            >
              {total === 0 ? (
                <KanbanLaneEmpty />
              ) : (
                <>
                  {cards.map((card) => (
                    <CrmCard key={card.id} card={card} onClick={() => onCardClick(card)} />
                  ))}
                  {restantes > 0 && (
                    <button
                      type="button"
                      onClick={() => showMore(c.id)}
                      className="mx-1 mt-1 inline-flex items-center justify-center gap-1 rounded-md bg-surface2 px-2 py-1.5 text-[0.78rem] font-medium text-text-2 transition-colors hover:bg-surface3 hover:text-text"
                    >
                      <ChevronDown size={13} strokeWidth={1.75} />
                      Ver mais ({restantes})
                    </button>
                  )}
                </>
              )}
            </KanbanLane>
          );
        })}
      </div>
    </div>
  );
}
